import React, { useState, useEffect, useMemo } from 'react';
import { Pencil, Trash2, Palette, Check, RotateCcw, Layers, Image as ImageIcon, Upload, X, Building2, MapPin, Plus, Users } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { useAuthStore } from '../../../store/authStore';
import { useDepartmentStore } from '../../../store/departmentStore';
import { useDesignationStore } from '../../../store/designationStore';
import { useAttendanceStore, Office, OfficeInput } from '../../../store/attendanceStore';
import { useAdminStore } from '../../../store/adminStore';
import { useBrandingStore, BRAND_COLOR_PRESETS } from '../../../store/brandingStore';
import { AdminPageHeader } from '../components';
import toast from 'react-hot-toast';

// Selectable option lists for the Regional & Localization defaults. Values are
// stored verbatim in public.tenants.settings->'profile'.
const CURRENCY_OPTIONS = [
  'INR — Indian Rupee (₹)',
  'USD — US Dollar ($)',
  'EUR — Euro (€)',
  'GBP — British Pound (£)',
  'AED — UAE Dirham (د.إ)',
  'SGD — Singapore Dollar (S$)',
  'AUD — Australian Dollar (A$)',
  'CAD — Canadian Dollar (C$)',
  'JPY — Japanese Yen (¥)',
  'CNY — Chinese Yuan (¥)',
];

const TIMEZONE_OPTIONS = [
  'Asia/Kolkata (IST +05:30)',
  'UTC (+00:00)',
  'America/New_York (EST −05:00)',
  'America/Los_Angeles (PST −08:00)',
  'Europe/London (GMT +00:00)',
  'Europe/Berlin (CET +01:00)',
  'Asia/Dubai (GST +04:00)',
  'Asia/Singapore (SGT +08:00)',
  'Asia/Tokyo (JST +09:00)',
  'Australia/Sydney (AEST +10:00)',
];

// Allowed office location types — must match the CHECK constraint on
// people.offices.location_type.
const LOCATION_TYPE_OPTIONS = [
  { value: 'OFFICE', label: 'Office' },
  { value: 'BRANCH', label: 'Branch' },
  { value: 'CLIENT_SITE', label: 'Client Site' },
  { value: 'WFH', label: 'Work From Home' },
  { value: 'REMOTE', label: 'Remote' },
  { value: 'OTHER', label: 'Other' },
];

const LANGUAGE_OPTIONS = [
  'English (UK)',
  'English (US)',
  'Hindi',
  'Tamil',
  'Telugu',
  'Kannada',
  'Malayalam',
  'French',
  'German',
  'Spanish',
  'Arabic',
  'Chinese (Simplified)',
  'Japanese',
];

export function OrganizationSettings() {
  const { user } = useAuthStore();
  const { departments, fetchDepartments, createDepartment, isLoading: isDeptLoading } = useDepartmentStore();
  const { designations, fetchDesignations, createDesignation } = useDesignationStore();
  const { offices, fetchOffices, saveOffice, deleteOffice } = useAttendanceStore();
  const { users, loadUsers, organization, loadOrganization, updateOrganization } = useAdminStore();

  // Connect to persistent global branding store
  const {
    primaryColor: storePrimary,
    secondaryColor: storeSecondary,
    accentColor: storeAccent,
    logoUrl: storeLogoUrl,
    faviconUrl: storeFaviconUrl,
    appName: storeAppName,
    tagline: storeTagline,
    activePresetId,
    setPrimaryColor,
    setSecondaryColor,
    setAccentColor,
    setLogoUrl,
    setFaviconUrl,
    setAppName,
    setTagline,
    applyPreset,
    applyBranding,
    resetBranding,
    saveBrandingToServer
  } = useBrandingStore();

  const [activeTab, setActiveTab] = useState<'profile' | 'departments' | 'locations' | 'job_levels' | 'branding'>('profile');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(new Date());

  // Tenant identity — the code is display-only; name/details come from the DB.
  const tenantCode = organization?.code || organization?.tenant_code || user?.tenantCode || '—';

  // Profile form is hydrated from the database (public.tenants). No fake defaults:
  // fields start blank and are filled from the real tenant row once it loads.
  const [profileForm, setProfileForm] = useState({
    name: '',
    legalName: '',
    domain: '',
    industry: '',
    email: '',
    phone: '',
    // Address
    line1: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    // Regional Defaults
    defaultCurrency: '',
    defaultTimezone: '',
    defaultLanguage: '',
    fiscalYearStart: '',
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingBranding, setIsSavingBranding] = useState(false);

  // Hydrate the profile form from the loaded tenant: name column + settings.profile.
  useEffect(() => {
    if (!organization) return;
    const p = organization.settings?.profile || {};
    setProfileForm({
      name: organization.name || '',
      legalName: p.legalName || organization.name || '',
      domain: p.domain || '',
      industry: p.industry || '',
      email: p.email || '',
      phone: p.phone || '',
      line1: p.line1 || '',
      city: p.city || '',
      state: p.state || '',
      country: p.country || '',
      postalCode: p.postalCode || '',
      defaultCurrency: p.defaultCurrency || '',
      defaultTimezone: p.defaultTimezone || '',
      defaultLanguage: p.defaultLanguage || '',
      fiscalYearStart: p.fiscalYearStart || '',
    });
  }, [organization]);

  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      toast.error('Company / tenant name is required');
      return;
    }
    setIsSavingProfile(true);
    try {
      const { name, ...profile } = profileForm;
      await updateOrganization({ name: name.trim(), profile });
      toast.success('Organization details saved');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save organization details');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSaveBranding = async () => {
    setIsSavingBranding(true);
    try {
      await saveBrandingToServer();
      toast.success('Branding saved — it will now apply on every device');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save branding');
    } finally {
      setIsSavingBranding(false);
    }
  };

  // Department Modal State
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });

  // Location/Office Modal State
  const [isOfficeModalOpen, setIsOfficeModalOpen] = useState(false);
  const [editingOfficeId, setEditingOfficeId] = useState<string | null>(null);
  const [officeForm, setOfficeForm] = useState<OfficeInput>({
    name: '',
    code: '',
    address: '',
    latitude: 12.9716,
    longitude: 77.5946,
    radius: 100,
    locationType: 'OFFICE',
  });

  // Local branding colors
  const [localPrimary, setLocalPrimary] = useState(storePrimary);
  const [localSecondary, setLocalSecondary] = useState(storeSecondary);
  const [localAccent, setLocalAccent] = useState(storeAccent || '#2563EB');
  const [selectedPreset, setSelectedPreset] = useState(activePresetId);

  useEffect(() => {
    fetchDepartments();
    fetchOffices();
    loadUsers();
    loadOrganization();
    fetchDesignations();
  }, [fetchDepartments, fetchOffices, loadUsers, loadOrganization, fetchDesignations]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([fetchDepartments(), fetchOffices(), loadUsers(), loadOrganization(), fetchDesignations()]);
      setLastRefreshed(new Date());
      toast.success('Organization data refreshed');
    } catch {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Job Levels are the people.designations master rows. Employee counts are
  // derived by matching each designation title against users' designation.
  const jobLevels = useMemo(() => {
    const counts = new Map<string, number>();
    users.forEach((u) => {
      const t = (u.designation || '').toLowerCase();
      if (t) counts.set(t, (counts.get(t) || 0) + 1);
    });
    return designations
      .map((d) => ({
        id: d.id,
        title: d.title,
        level: d.level,
        department: d.departmentName,
        count: counts.get(d.title.toLowerCase()) || 0,
      }))
      .sort((a, b) => b.level - a.level);
  }, [designations, users]);

  // Create Job (designation) modal state
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isSavingJob, setIsSavingJob] = useState(false);
  const [jobForm, setJobForm] = useState<{ title: string; level: string; departmentId: string }>({
    title: '',
    level: '1',
    departmentId: '',
  });

  const handleSaveJob = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobForm.title.trim()) {
      toast.error('Job title is required');
      return;
    }
    setIsSavingJob(true);
    try {
      await createDesignation({
        title: jobForm.title.trim(),
        level: Number(jobForm.level) || 1,
        departmentId: jobForm.departmentId || null,
      });
      toast.success(`Job level "${jobForm.title}" created`);
      setIsJobModalOpen(false);
      setJobForm({ title: '', level: '1', departmentId: '' });
      fetchDesignations();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create job level');
    } finally {
      setIsSavingJob(false);
    }
  };

  // Department Handlers
  const handleSaveDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deptForm.name.trim()) {
      toast.error('Department name is required');
      return;
    }
    try {
      await createDepartment(deptForm.name.trim(), deptForm.description.trim());
      toast.success(`Department "${deptForm.name}" created`);
      setIsDeptModalOpen(false);
      setDeptForm({ name: '', description: '' });
      fetchDepartments();
    } catch {
      toast.error('Failed to create department');
    }
  };

  // Office Handlers
  const handleOpenOfficeModal = (office?: Office) => {
    if (office) {
      setEditingOfficeId(office.id);
      setOfficeForm({
        name: office.name,
        code: office.code || '',
        address: office.address || '',
        latitude: office.latitude || 12.9716,
        longitude: office.longitude || 77.5946,
        radius: office.radius || 100,
        locationType: office.locationType || 'OFFICE',
      });
    } else {
      setEditingOfficeId(null);
      setOfficeForm({
        name: '',
        code: '',
        address: '',
        latitude: 12.9716,
        longitude: 77.5946,
        radius: 100,
        locationType: 'OFFICE',
      });
    }
    setIsOfficeModalOpen(true);
  };

  const handleSaveOffice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!officeForm.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    try {
      await saveOffice(officeForm, editingOfficeId || undefined);
      toast.success(editingOfficeId ? 'Location updated' : 'Location registered');
      setIsOfficeModalOpen(false);
      fetchOffices();
    } catch {
      toast.error('Failed to save location');
    }
  };

  const handleDeleteOffice = async (id: string, name: string) => {
    if (!window.confirm(`Delete office location "${name}"?`)) return;
    try {
      await deleteOffice(id);
      toast.success(`Location "${name}" removed`);
      fetchOffices();
    } catch {
      toast.error('Failed to delete location');
    }
  };

  const handleSelectPreset = (presetId: string) => {
    const preset = BRAND_COLOR_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setSelectedPreset(preset.id);
    setLocalPrimary(preset.primary);
    setLocalSecondary(preset.secondary);
    setLocalAccent(preset.accent || preset.primary);
    applyPreset(preset.id);
    toast.success(`Brand preset "${preset.name}" applied`);
  };

  const handleApplyCustomBranding = () => {
    applyBranding({
      primaryColor: localPrimary,
      secondaryColor: localSecondary,
      accentColor: localAccent,
      activePresetId: 'custom',
    });
    setSelectedPreset('custom');
    toast.success('Custom brand palette applied');
  };

  const handleResetDefaults = () => {
    resetBranding();
    setLocalPrimary('#0F62FE');
    setLocalSecondary('#393939');
    setLocalAccent('#1192E8');
    setSelectedPreset('ibm-blue');
    toast.success('Reset brand palette to standard IBM Carbon theme.');
  };

  // Read an uploaded image as a base64 data URL so it survives a refresh via
  // localStorage (no file-storage backend exists yet). `maxKb` guards the
  // localStorage quota — favicons are tiny, logos should stay lightweight.
  const readImageAsDataUrl = (
    e: React.ChangeEvent<HTMLInputElement>,
    maxKb: number,
    onDone: (dataUrl: string) => void,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > maxKb * 1024) {
      toast.error(`Image is too large — keep it under ${maxKb} KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onDone(String(reader.result));
    reader.onerror = () => toast.error('Could not read that image.');
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) =>
    readImageAsDataUrl(e, 512, (url) => {
      setLogoUrl(url);
      toast.success('Logo updated across the app.');
    });

  const handleFaviconUpload = (e: React.ChangeEvent<HTMLInputElement>) =>
    readImageAsDataUrl(e, 128, (url) => {
      setFaviconUrl(url);
      toast.success('Favicon updated — check the browser tab.');
    });

  return (
    <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-12">
      {/* Page Header */}
      <AdminPageHeader
        icon={Building2}
        title="Organization Settings"
        subtitle="Manage legal profile, multi-location sites, departments, job hierarchy and enterprise branding"
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastRefreshed={lastRefreshed}
        badge={
          <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20 font-medium">
            Tenant: {tenantCode}
          </Badge>
        }
      />

      <div className="space-y-4">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 border-b border-border pb-px text-xs flex-wrap">
          {[
            { id: 'profile', label: 'Profile & Regional Defaults', icon: Building2 },
            { id: 'departments', label: `Departments (${departments.length})`, icon: Layers },
            { id: 'locations', label: `Locations & Offices (${offices.length})`, icon: MapPin },
            { id: 'job_levels', label: `Job Levels (${jobLevels.length})`, icon: Users },
            { id: 'branding', label: 'Enterprise Branding', icon: Palette },
          ].map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1.5 px-4 py-2 border-b-2 font-medium transition-colors cursor-pointer ${
                  isActive
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <TabIcon className="size-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* TAB 1: PROFILE, ADDRESS & REGIONAL DEFAULTS */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Profile Section */}
            <Card className="bg-card border-border shadow-xs">
              <CardHeader className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Organization Profile
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Core identity and contact details configured for tenant #{tenantCode}
                </p>
              </CardHeader>
              <CardContent className="p-4 space-y-4 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Company / Tenant Name</label>
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Legal Entity Name</label>
                    <input
                      type="text"
                      value={profileForm.legalName}
                      onChange={(e) => setProfileForm({ ...profileForm, legalName: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Corporate Domain</label>
                    <input
                      type="text"
                      value={profileForm.domain}
                      onChange={(e) => setProfileForm({ ...profileForm, domain: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground font-mono outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Admin Contact Email</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Primary Phone</label>
                    <input
                      type="text"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Industry Vertical</label>
                    <input
                      type="text"
                      value={profileForm.industry}
                      onChange={(e) => setProfileForm({ ...profileForm, industry: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Address Section */}
            <Card className="bg-card border-border shadow-xs">
              <CardHeader className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Headquarters &amp; Registered Address
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Official registered office address for compliance and billing invoices
                </p>
              </CardHeader>
              <CardContent className="p-4 space-y-4 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-foreground block mb-1">Address Line 1</label>
                    <input
                      type="text"
                      value={profileForm.line1}
                      onChange={(e) => setProfileForm({ ...profileForm, line1: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">City</label>
                    <input
                      type="text"
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Postal / ZIP Code</label>
                    <input
                      type="text"
                      value={profileForm.postalCode}
                      onChange={(e) => setProfileForm({ ...profileForm, postalCode: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regional Defaults Section */}
            <Card className="bg-card border-border shadow-xs">
              <CardHeader className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Regional &amp; Localization Defaults
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Platform currency, timezone and fiscal calendar defaults
                </p>
              </CardHeader>
              <CardContent className="p-4 space-y-4 text-xs">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Default Currency</label>
                    <select
                      value={profileForm.defaultCurrency}
                      onChange={(e) => setProfileForm({ ...profileForm, defaultCurrency: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs cursor-pointer"
                    >
                      <option value="">Select currency…</option>
                      {profileForm.defaultCurrency && !CURRENCY_OPTIONS.includes(profileForm.defaultCurrency) && (
                        <option value={profileForm.defaultCurrency}>{profileForm.defaultCurrency}</option>
                      )}
                      {CURRENCY_OPTIONS.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">System Timezone</label>
                    <select
                      value={profileForm.defaultTimezone}
                      onChange={(e) => setProfileForm({ ...profileForm, defaultTimezone: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs cursor-pointer"
                    >
                      <option value="">Select timezone…</option>
                      {profileForm.defaultTimezone && !TIMEZONE_OPTIONS.includes(profileForm.defaultTimezone) && (
                        <option value={profileForm.defaultTimezone}>{profileForm.defaultTimezone}</option>
                      )}
                      {TIMEZONE_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Primary Language</label>
                    <select
                      value={profileForm.defaultLanguage}
                      onChange={(e) => setProfileForm({ ...profileForm, defaultLanguage: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs cursor-pointer"
                    >
                      <option value="">Select language…</option>
                      {profileForm.defaultLanguage && !LANGUAGE_OPTIONS.includes(profileForm.defaultLanguage) && (
                        <option value={profileForm.defaultLanguage}>{profileForm.defaultLanguage}</option>
                      )}
                      {LANGUAGE_OPTIONS.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Fiscal Year Start</label>
                    <input
                      type="text"
                      value={profileForm.fiscalYearStart}
                      onChange={(e) => setProfileForm({ ...profileForm, fiscalYearStart: e.target.value })}
                      className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Logo preview + jump to branding */}
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="w-20 h-20 rounded-lg flex items-center justify-center mx-auto mb-3 bg-secondary border border-border overflow-hidden">
                    <img
                      src={storeLogoUrl && storeLogoUrl.trim() ? storeLogoUrl : '/brand/c1-logo.png'}
                      alt="Organization logo"
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Organization Logo</p>
                  <Button variant="secondary" size="sm" className="w-full" onClick={() => setActiveTab('branding')}>
                    Change in Branding
                  </Button>
                </CardContent>
              </Card>

              {/* Favicon preview + jump to branding */}
              <Card>
                <CardContent className="p-4 text-center">
                  <div className="w-20 h-20 rounded-lg flex items-center justify-center mx-auto mb-3 bg-secondary border border-border overflow-hidden">
                    <img
                      src={storeFaviconUrl && storeFaviconUrl.trim() ? storeFaviconUrl : '/favicon.png'}
                      alt="Browser favicon"
                      className="size-10 object-contain"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Browser Favicon</p>
                  <Button variant="secondary" size="sm" className="w-full" onClick={() => setActiveTab('branding')}>
                    Change in Branding
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Save organization profile to the database (public.tenants) */}
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                className="gap-1.5"
              >
                <Check className="size-3.5" />
                <span>{isSavingProfile ? 'Saving…' : 'Save Organization Details'}</span>
              </Button>
            </div>
          </div>
        )}

        {/* TAB 2: DEPARTMENTS (Manageable Table from DB) */}
        {activeTab === 'departments' && (
          <Card className="bg-card border-border shadow-xs">
            <CardHeader className="p-4 border-b border-border flex flex-row items-center justify-between space-y-0">
              <div>
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Organizational Departments
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Master departments loaded from people.departments table
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsDeptModalOpen(true)}
                className="gap-1.5"
              >
                <Plus className="size-3.5" />
                <span>Add Department</span>
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="py-3 px-4">Department Name</th>
                      <th className="py-3 px-4">Description</th>
                      <th className="py-3 px-4 text-center">Active Members</th>
                      <th className="py-3 px-4 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {departments.length > 0 ? (
                      departments.map((d) => (
                        <tr key={d.id} className="hover:bg-secondary/20 transition-colors">
                          <td className="py-3 px-4 font-semibold text-foreground">
                            {d.name}
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {d.description || '—'}
                          </td>
                          <td className="py-3 px-4 text-center font-mono font-medium">
                            {d.employeeCount}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">
                              Active
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-xs text-muted-foreground">
                          No departments configured yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

      {/* BRANDING & THEME TAB — FULLY WIRED TO ASSETS, SUPPORT & PLATFORM */}
      {activeTab === 'branding' && (
        <div className="space-y-6">
          {/* 0. Brand Identity — name, logo & favicon */}
          <Card>
            <CardHeader className="bg-card/30 px-6 py-4">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <ImageIcon className="size-4 text-primary" /> Brand Identity
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Set the application name, logo (sidebar, sign-in, dashboard) and the browser-tab
                favicon. Changes apply instantly and are saved for this browser.
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Application name & tagline */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                    Application Name
                  </label>
                  <Input
                    value={storeAppName}
                    onChange={(e) => setAppName(e.target.value)}
                    placeholder="TeamOne"
                    className="text-sm"
                    maxLength={40}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Shown in the sidebar, dashboard header, sign-in page, logo wordmark and browser tab.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                    Tagline
                  </label>
                  <Input
                    value={storeTagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Business Platform"
                    className="text-sm"
                    maxLength={48}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    The small line under the logo wordmark (sign-in page and expanded logo).
                  </p>
                </div>
              </div>

              <div className="border-t border-border" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-2">
                  Application Logo
                </label>
                <div className="flex items-center gap-4">
                  <div className="size-16 rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                    <img
                      src={storeLogoUrl && storeLogoUrl.trim() ? storeLogoUrl : '/brand/c1-logo.png'}
                      alt="Current logo"
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <label className="inline-flex">
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                        <span className="inline-flex items-center gap-1.5 cursor-pointer rounded-lg bg-card border border-border px-3 py-2 text-xs font-semibold text-foreground hover:bg-secondary/50 transition-colors">
                          <Upload className="size-3.5" /> Upload logo
                        </span>
                      </label>
                      {storeLogoUrl && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setLogoUrl('');
                            toast.success('Logo reset to default.');
                          }}
                        >
                          <X className="size-3.5 mr-1" /> Reset
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">PNG or SVG, square, under 512 KB.</p>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-[11px] font-semibold text-muted-foreground block mb-1">
                    …or paste an image URL
                  </label>
                  <Input
                    value={storeLogoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="/brand/c1-logo.png or https://…"
                    className="font-mono text-xs"
                  />
                </div>
              </div>

              {/* Favicon */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-2">
                  Browser Favicon
                </label>
                <div className="flex items-center gap-4">
                  <div className="size-16 rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                    <img
                      src={storeFaviconUrl && storeFaviconUrl.trim() ? storeFaviconUrl : '/favicon.png'}
                      alt="Current favicon"
                      className="size-8 object-contain"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      <label className="inline-flex">
                        <input type="file" accept="image/*" className="hidden" onChange={handleFaviconUpload} />
                        <span className="inline-flex items-center gap-1.5 cursor-pointer rounded-lg bg-card border border-border px-3 py-2 text-xs font-semibold text-foreground hover:bg-secondary/50 transition-colors">
                          <Upload className="size-3.5" /> Upload favicon
                        </span>
                      </label>
                      {storeFaviconUrl && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setFaviconUrl('');
                            toast.success('Favicon reset to default.');
                          }}
                        >
                          <X className="size-3.5 mr-1" /> Reset
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Square PNG/ICO, under 128 KB.</p>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-[11px] font-semibold text-muted-foreground block mb-1">
                    …or paste an image URL
                  </label>
                  <Input
                    value={storeFaviconUrl}
                    onChange={(e) => setFaviconUrl(e.target.value)}
                    placeholder="/favicon.png or https://…"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
              </div>

              {/* Persist brand identity + colors to the database */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveBranding}
                  disabled={isSavingBranding}
                  className="gap-1.5"
                >
                  <Check className="size-3.5" />
                  <span>{isSavingBranding ? 'Saving…' : 'Save Branding'}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* TAB 3: LOCATIONS & OFFICES */}
        {activeTab === 'locations' && (
          <div className="space-y-6">
          <Card>
            <CardHeader className="bg-card/30 px-6 py-4 flex flex-row items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Office Sites &amp; Work Locations
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Physical and remote office nodes configured in people.offices table
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleOpenOfficeModal()}
                className="gap-1.5"
              >
                <Plus className="size-3.5" />
                <span>Add Location</span>
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="py-3 px-4">Location Name</th>
                      <th className="py-3 px-4">Site Code</th>
                      <th className="py-3 px-4">Address / Coordinates</th>
                      <th className="py-3 px-4">Location Type</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {offices.length > 0 ? (
                      offices.map((loc) => (
                        <tr key={loc.id} className="hover:bg-secondary/20 transition-colors">
                          <td className="py-3 px-4 font-semibold text-foreground">
                            <div className="flex items-center gap-2">
                              <MapPin className="size-3.5 text-primary" />
                              <span>{loc.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-mono text-muted-foreground">
                            {loc.code || '—'}
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {loc.address || `${loc.latitude}, ${loc.longitude}`}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-[10px] bg-secondary text-foreground uppercase">
                              {loc.locationType || 'OFFICE'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right space-x-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenOfficeModal(loc)}
                              className="h-7 px-2 text-xs"
                            >
                              <Pencil className="size-3 mr-1" />
                              <span>Edit</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteOffice(loc.id, loc.name)}
                              className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="size-3 mr-1" />
                              <span>Delete</span>
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-xs text-muted-foreground">
                          No locations configured yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* TAB 4: JOB LEVELS & DESIGNATIONS */}
        {activeTab === 'job_levels' && (
          <Card className="bg-card border-border shadow-xs">
            <CardHeader className="p-4 border-b border-border flex flex-row items-center justify-between space-y-0">
              <div>
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Job Levels &amp; Designation Hierarchy
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Job titles from people.designations — used in the employee Designation picker
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsJobModalOpen(true)}
                className="gap-1.5"
              >
                <Plus className="size-3.5" />
                <span>Create Job</span>
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="py-3 px-4">Designation Title</th>
                      <th className="py-3 px-4">Department</th>
                      <th className="py-3 px-4">Authority Tier</th>
                      <th className="py-3 px-4 text-center">Assigned Employees</th>
                      <th className="py-3 px-4 text-right">Clearance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {jobLevels.length > 0 ? (
                      jobLevels.map((jl) => (
                        <tr key={jl.id} className="hover:bg-secondary/20 transition-colors">
                          <td className="py-3 px-4 font-semibold text-foreground">
                            {jl.title}
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {jl.department || '—'}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="text-[10px] font-mono bg-secondary text-foreground">
                              Level {jl.level}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center font-mono font-medium">
                            {jl.count}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                jl.level >= 70
                                  ? 'bg-primary/10 text-primary border-primary/30'
                                  : 'bg-secondary text-muted-foreground'
                              }`}
                            >
                              {jl.level >= 90 ? 'Executive' : jl.level >= 50 ? 'Management' : 'Operational'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-xs text-muted-foreground">
                          No job levels yet — click “Create Job” to add the first one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* TAB 5: ENTERPRISE BRANDING */}
        {activeTab === 'branding' && (
          <div className="space-y-6">
            <Card className="bg-card border-border shadow-xs">
              <CardHeader className="p-4 border-b border-border">
                <h2 className="text-sm font-semibold font-display text-foreground">
                  Color Presets &amp; Enterprise Palettes
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Select a standardized enterprise palette or fine-tune brand tokens
                </p>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {BRAND_COLOR_PRESETS.map((preset) => {
                    const isSelected = selectedPreset === preset.id;
                    return (
                      <div
                        key={preset.id}
                        onClick={() => handleSelectPreset(preset.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/40 shadow-xs'
                            : 'border-border hover:border-border/80 bg-card'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-xs text-foreground">{preset.name}</span>
                          {isSelected && <Check className="size-3.5 text-primary stroke-[3]" />}
                        </div>
                        <div className="flex gap-1.5 h-4 rounded overflow-hidden">
                          <div className="flex-1" style={{ backgroundColor: preset.primary }} />
                          <div className="flex-1" style={{ backgroundColor: preset.secondary }} />
                          <div className="flex-1" style={{ backgroundColor: preset.accent || preset.primary }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Custom Hex Pickers */}
                <div className="pt-4 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Primary Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={localPrimary}
                        onChange={(e) => setLocalPrimary(e.target.value)}
                        className="size-8 rounded border border-border cursor-pointer bg-card"
                      />
                      <input
                        type="text"
                        value={localPrimary}
                        onChange={(e) => setLocalPrimary(e.target.value)}
                        className="w-full h-8 rounded border border-border bg-card px-2 text-xs font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Secondary / Dark</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={localSecondary}
                        onChange={(e) => setLocalSecondary(e.target.value)}
                        className="size-8 rounded border border-border cursor-pointer bg-card"
                      />
                      <input
                        type="text"
                        value={localSecondary}
                        onChange={(e) => setLocalSecondary(e.target.value)}
                        className="w-full h-8 rounded border border-border bg-card px-2 text-xs font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-foreground block mb-1">Accent Accent</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={localAccent}
                        onChange={(e) => setLocalAccent(e.target.value)}
                        className="size-8 rounded border border-border cursor-pointer bg-card"
                      />
                      <input
                        type="text"
                        value={localAccent}
                        onChange={(e) => setLocalAccent(e.target.value)}
                        className="w-full h-8 rounded border border-border bg-card px-2 text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
                  <Button variant="secondary" size="sm" onClick={handleResetDefaults}>
                    <RotateCcw className="size-3.5 mr-1" />
                    <span>Reset Defaults</span>
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleApplyCustomBranding}>
                    <span>Apply Brand Palette</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Add Department Modal */}
      {isDeptModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-2xs p-4 animate-in fade-in">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h2 className="font-display text-section-title text-foreground">
                Register New Department
              </h2>
              <button
                type="button"
                onClick={() => setIsDeptModalOpen(false)}
                className="size-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveDepartment} className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Department Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Infrastructure & Security"
                  value={deptForm.name}
                  onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Description</label>
                <textarea
                  rows={3}
                  placeholder="Department responsibilities and scope..."
                  value={deptForm.description}
                  onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                  className="w-full rounded-lg border border-border bg-card p-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button variant="secondary" size="sm" type="button" onClick={() => setIsDeptModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Save Department
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit Location Modal */}
      {isOfficeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-2xs p-4 animate-in fade-in">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h2 className="font-display text-section-title text-foreground">
                {editingOfficeId ? 'Edit Location' : 'Register New Location'}
              </h2>
              <button
                type="button"
                onClick={() => setIsOfficeModalOpen(false)}
                className="size-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveOffice} className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Location / Office Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Bangalore Tech Hub"
                  value={officeForm.name}
                  onChange={(e) => setOfficeForm({ ...officeForm, name: e.target.value })}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Site Code</label>
                <input
                  type="text"
                  placeholder="e.g. BLR-01"
                  value={officeForm.code || ''}
                  onChange={(e) => setOfficeForm({ ...officeForm, code: e.target.value })}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Location Type</label>
                <select
                  value={officeForm.locationType || 'OFFICE'}
                  onChange={(e) => setOfficeForm({ ...officeForm, locationType: e.target.value })}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs cursor-pointer"
                >
                  {LOCATION_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Physical Address</label>
                <textarea
                  rows={2}
                  placeholder="Full street address..."
                  value={officeForm.address || ''}
                  onChange={(e) => setOfficeForm({ ...officeForm, address: e.target.value })}
                  className="w-full rounded-lg border border-border bg-card p-2.5 text-xs text-foreground outline-none focus:border-primary shadow-2xs resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button variant="secondary" size="sm" type="button" onClick={() => setIsOfficeModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  {editingOfficeId ? 'Update Location' : 'Register Location'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Job (Designation) Modal */}
      {isJobModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-2xs p-4 animate-in fade-in">
          <div className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h2 className="font-display text-section-title text-foreground">
                Create New Job Level
              </h2>
              <button
                type="button"
                onClick={() => setIsJobModalOpen(false)}
                className="size-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleSaveJob} className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Job Title / Designation</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Senior Software Engineer"
                  value={jobForm.title}
                  onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Department</label>
                <select
                  value={jobForm.departmentId}
                  onChange={(e) => setJobForm({ ...jobForm, departmentId: e.target.value })}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs cursor-pointer"
                >
                  <option value="">No specific department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1">Authority Level (1–100)</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={jobForm.level}
                  onChange={(e) => setJobForm({ ...jobForm, level: e.target.value })}
                  className="w-full h-9 rounded-lg border border-border bg-card px-3 text-xs text-foreground outline-none focus:border-primary shadow-2xs"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Higher = more senior. 90+ Executive, 50+ Management, below that Operational.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button variant="secondary" size="sm" type="button" onClick={() => setIsJobModalOpen(false)} disabled={isSavingJob}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={isSavingJob}>
                  {isSavingJob ? 'Creating…' : 'Create Job'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
